import {
    useEffect,
    useState,
    useMemo,
    type FC
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type IFlagProps from "./type";
import stylesheet, {
    getIsFlagEmbedded,
    loadEmbeddedFlag,
    getIsFlagBlocked,
    getFlagImageUri,
    FLAG_XML_CACHE,
    getFlagCached,
    getFlagLabel,
    getFlagCode,
    getFlagUri,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useAnimatedState
} from "../../helpers/motion";
import {
    fetchText,
    SvgXml
} from "react-native-svg";
import {
    getIconSize
} from "../icon/stylesheet";

const Flag: FC<IFlagProps> = ({
    "aria-label": ariaLabel,
    isDecorative = false,
    accessibilityLabel,
    size = "medium",
    customLocalize,
    customTheme,
    baseUrl,
    style,
    code,
    ...props
}) => {
    const {
        iconSizes,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const currentCode = getFlagCode({
        code
    });

    const currentUri = getFlagUri({
        code: currentCode,
        baseUrl
    });

    const [
        loadedUri,
        setLoadedUri
    ] = useState<string | undefined>(() => getFlagCached({
        uri: currentUri
    }) ? currentUri : undefined);

    const [
        erroredUri,
        setErroredUri
    ] = useState<string>();

    const [
        xmlUri,
        setXmlUri
    ] = useState<string>();

    const isWeb = Platform.OS === "web";

    const isEmbedded = getIsFlagEmbedded({
        uri: currentUri
    });

    const flagXml = currentUri && !isWeb ? FLAG_XML_CACHE.get(currentUri) : undefined;

    const imageUri = isWeb && (!isEmbedded || xmlUri === currentUri || FLAG_XML_CACHE.has(currentUri ?? "")) ? getFlagImageUri({
        uri: currentUri
    }) : undefined;

    const isLoaded = isWeb ? !!currentUri && loadedUri === currentUri : !!flagXml;

    const isErrored = !currentUri || erroredUri === currentUri;

    const imageOpacity = useAnimatedState({
        value: isLoaded && !isErrored ? 1 : 0,
        intent: "surfaceEnter",
        motion: "fade",
        customTheme
    });

    const placeholderOpacity = useMemo(() => imageOpacity.interpolate({
        outputRange: [
            1,
            0
        ],
        inputRange: [
            0,
            1
        ]
    }), [
        imageOpacity
    ]);

    const currentLabel = useMemo(() => {
        if(isDecorative) {
            return undefined;
        }

        return ariaLabel ?? accessibilityLabel ?? getFlagLabel({
            locale: activeLocale,
            code: currentCode
        });
    }, [
        accessibilityLabel,
        isDecorative,
        activeLocale,
        currentCode,
        ariaLabel
    ]);

    const currentSize = getIconSize({
        iconSizes,
        size
    });

    const {
        placeholder: placeholderDynamicStyle,
        container: containerDynamicStyle,
        image: imageDynamicStyle
    } = useStyles({
        placeholderColor: colors.system.state.overlay.hover.default,
        size: currentSize
    });

    useEffect(() => {
        if((isWeb && !isEmbedded) || !currentUri || FLAG_XML_CACHE.has(currentUri)) {
            return;
        }

        let isActive = true;

        const request = isEmbedded ? loadEmbeddedFlag({
            uri: currentUri
        }) : fetchText(currentUri);

        request.then((xml) => {
            if(!xml || !xml.includes("<svg")) {
                throw new Error(`Flag: "${currentUri}" is not an SVG file.`);
            }

            FLAG_XML_CACHE.set(currentUri, xml);

            if(isActive) {
                setXmlUri(currentUri);

                if(!isWeb) {
                    setLoadedUri(currentUri);
                }
            }
        }).catch(() => {
            if(isActive) {
                setErroredUri(currentUri);
            }
        });

        return () => {
            isActive = false;
        };
    }, [
        currentUri
    ]);

    const handleImageLoad = () => {
        setLoadedUri(currentUri);
    };

    const handleImageError = () => {
        setErroredUri(currentUri);
    };

    const renderImage = () => {
        if(isErrored) {
            return null;
        }

        if(isWeb) {
            if(!imageUri) {
                return null;
            }

            return <Animated.Image
                style={[
                    imageDynamicStyle,
                    {
                        opacity: imageOpacity
                    }
                ]}
                onError={handleImageError}
                onLoad={handleImageLoad}
                aria-hidden={true}
                source={{
                    uri: imageUri
                }}
            />;
        }

        if(!flagXml) {
            return null;
        }

        return <Animated.View
            style={[
                imageDynamicStyle,
                {
                    opacity: imageOpacity
                }
            ]}
        >
            <SvgXml
                onError={handleImageError}
                height={currentSize}
                width={currentSize}
                xml={flagXml}
            />
        </Animated.View>;
    };

    if(getIsFlagBlocked({
        code: currentCode
    })) {
        return null;
    }

    return <View
        {...props}
        aria-hidden={isDecorative ? true : undefined}
        role={isDecorative ? undefined : "img"}
        accessible={!isDecorative}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={currentLabel}
    >
        <Animated.View
            style={[
                stylesheet.placeholder,
                placeholderDynamicStyle,
                {
                    opacity: placeholderOpacity
                }
            ]}
        />
        {renderImage()}
    </View>;
};
export default Flag;
